import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { pushTo } from '@app/core/routing';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { useAvailableExportPlatforms } from '@app/modules/fragments/screens/view/components/social-export-panorama/hooks/useAvailableExportPlatforms';
import { fragmentsPermissions, webApi } from '@mi-front/api-clients';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useFragment } from '../../../hooks/useFragment';

const noPlatformsToPublishMessage = defineMessage({
  id: 'fragments.socialExport.noPlatformsToPublish',
  defaultMessage: 'Sin plataformas para publicar',
});

export interface ProjectPublishDropdownButtonProps {
  /** Video editor project id passed to the upload route as `projectId` for source preselection. */
  projectId: string;
  /** Fragment social uploads grouped by platform; drives which platforms still allow a new publish. */
  uploadsByPlatform: Map<string, webApi.FragmentSocialUploadItemDto[]> | undefined;
}

/**
 * "Publicar en" dropdown for a generated project: lists platforms available for a new publish
 * (same rules as the publish tab) and navigates to the upload screen with this project preselected.
 */
export function ProjectPublishDropdownButton({
  projectId,
  uploadsByPlatform,
}: ProjectPublishDropdownButtonProps) {
  const { fragment } = useFragment();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { platforms: availablePlatforms } = useAvailableExportPlatforms(
    fragment.stationId,
    uploadsByPlatform,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    const platform = availablePlatforms.find(
      (p) => p.id.toUpperCase() === platformId.toUpperCase(),
    );
    if (platform) {
      pushTo(routeGenerator.upload(fragment, platform, projectId));
    }
    handleClose();
  };

  return (
    <IfPermission permission={fragmentsPermissions.Fragments_SocialPublish}>
      {availablePlatforms.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          <FormattedMessage {...noPlatformsToPublishMessage} />
        </Typography>
      ) : (
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            size="small"
            onClick={handleClick}
            startIcon={<ShareIcon />}
            endIcon={<ArrowDropDownIcon />}
          >
            <FormattedMessage id="fragments.socialExport.publishTo" defaultMessage="Publicar en" />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {availablePlatforms.map((platform) => (
              <MenuItem key={platform.id} onClick={() => handlePlatformSelect(platform.id)}>
                {platform.Icon({ sx: { mr: 1, fontSize: 20 }, colored: true })} {platform.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}
    </IfPermission>
  );
}
